export type PostKind = 'evento' | 'novita' | 'articolo';

export const POST_KIND_LABELS: Record<PostKind, string> = {
  evento: 'Evento',
  novita: 'Novità',
  articolo: 'Articolo',
};

/** Riga della tabella posts (supabase/migrations/20261101000000_posts.sql). */
export interface Post {
  id: string;
  kind: PostKind;
  slug: string;
  title: string;
  summary: string;
  image_url: string | null;
  /** Testo in HTML dall'editor formattato. */
  body: string;
  /** Solo eventi: giorno di inizio, "YYYY-MM-DD". */
  event_date: string | null;
  /** Solo eventi di più giorni: ultimo giorno, "YYYY-MM-DD". */
  event_end_date: string | null;
  event_time: string;
  location: string;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export type PostInput = Omit<Post, 'id' | 'created_at' | 'updated_at'>;

export const POST_SELECT =
  'id, kind, slug, title, summary, image_url, body, event_date, event_end_date, event_time, location, published, published_at, created_at, updated_at';

// Le date degli eventi sono giorni, senza fuso: si leggono e si scrivono in UTC
// perché "2026-11-08" non scivoli al giorno prima.
const formatDay = (day: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('it-IT', { ...options, timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`));

/** "sabato 8 novembre 2026", oppure "8 – 9 novembre 2026" se dura più giorni. */
export function eventDateLabel(start: string, end: string | null): string {
  if (!end || end === start) return formatDay(start, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  const sameMonth = sameYear && start.slice(0, 7) === end.slice(0, 7);
  const from = sameMonth
    ? formatDay(start, { day: 'numeric' })
    : formatDay(start, { day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }) });
  return `${from} – ${formatDay(end, { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

/** Data da mostrare su una scheda: il giorno dell'evento o la pubblicazione. */
export function postDateLabel(post: Post): string {
  if (post.kind === 'evento' && post.event_date) return eventDateLabel(post.event_date, post.event_end_date);
  return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Rome' }).format(
    new Date(post.published_at ?? post.created_at),
  );
}

/** Oggi in Italia, "YYYY-MM-DD": un evento resta "prossimo" per tutto il suo ultimo giorno. */
const todayInItaly = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(new Date());

export const isUpcomingEvent = (post: Post) =>
  post.kind === 'evento' && !!post.event_date && (post.event_end_date ?? post.event_date) >= todayInItaly();

/** I contenuti pubblicati divisi come sul sito, ognuno nel suo ordine. */
export function groupPosts(posts: Post[]) {
  const published = posts.filter((p) => p.published);
  const events = published.filter((p) => p.kind === 'evento');
  return {
    // Il più vicino per primo.
    upcoming: events.filter(isUpcomingEvent).sort((a, b) => a.event_date!.localeCompare(b.event_date!)),
    // Il più recente per primo.
    past: events.filter((p) => !isUpcomingEvent(p)).sort((a, b) => (b.event_date ?? '').localeCompare(a.event_date ?? '')),
    news: published
      .filter((p) => p.kind !== 'evento')
      .sort((a, b) => (b.published_at ?? b.created_at).localeCompare(a.published_at ?? a.created_at)),
  };
}
